/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {ListMenuContent} from './list-menu-content'
import {HeaderDown, HeaderUp} from '../icons'
import {Box, Text, useMultiStyleConfig, Slide, IconButton} from '@chakra-ui/react'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useLocation} from 'react-router-dom'
import {remCalc} from '../../theme/_utils'

const ListMenuPopover = ({
    primarycategory,
    categoriesData,
    selectedMenuItem,
    setSelectedMenuItem,
    onItemClick,
}) => {
    const styles = useMultiStyleConfig('ListMenu')
    const location = useLocation()

    const isOpen = selectedMenuItem === primarycategory.uid
    const [delayedIsOpen, setDelayedIsOpen] = useState(selectedMenuItem === primarycategory.uid)
    const menuCloseTimeoutRef = useRef(null)
    const isKeyboardNavRef = useRef(false)
    const wasClosedWithNoTimeoutRef = useRef(false)
    const previousLocation = useRef(location.pathname)

    const {url, label, isAbsolute} = getGlobalLink({
        link: primarycategory?.redirects,
        categoriesData,
    })

    const categoryID = primarycategory?.redirects?.categorysfcc.data?.[0]?.id ?? primarycategory.uid

    const prevSelectedMenuItemRef = useRef(selectedMenuItem)
    const menuContainerRef = useRef(null)
    const handleBlur = (event) => {
        // checks if the new focused element is outside of the menu container
        if (menuContainerRef.current && !menuContainerRef.current.contains(event.relatedTarget)) {
            // if it is outside, close the menu
            setSelectedMenuItem(null)
            isKeyboardNavRef.current = false
        }
    }
    // Effect to track location changes and close the menu on navigation
    useEffect(() => {
        if (previousLocation.current !== location.pathname) {
            setSelectedMenuItem(null)
            setDelayedIsOpen(false)
        }
        previousLocation.current = location.pathname
    }, [location.pathname, setSelectedMenuItem])

    useEffect(() => {
        if (isOpen) {
            if (menuCloseTimeoutRef.current) {
                clearTimeout(menuCloseTimeoutRef.current)
                menuCloseTimeoutRef.current = null
            }

            wasClosedWithNoTimeoutRef.current = false
            setDelayedIsOpen(true)
        } else {
            const isCompleteClose = selectedMenuItem === null
            const wasTabChange =
                prevSelectedMenuItemRef.current !== null && selectedMenuItem !== null

            if (isCompleteClose) {
                // Case 1: Closed with Escape key - close immediately
                if (wasClosedWithNoTimeoutRef.current) {
                    setDelayedIsOpen(false)
                    wasClosedWithNoTimeoutRef.current = false
                }
                // Case 2: Tab change - close immediately
                else if (wasTabChange) {
                    setDelayedIsOpen(false)
                }
                // Case 3: Mouse navigation - delayed close
                else {
                    menuCloseTimeoutRef.current = setTimeout(() => {
                        setDelayedIsOpen(false)
                    }, 500)
                }
            } else {
                setDelayedIsOpen(false)
            }
        }

        prevSelectedMenuItemRef.current = selectedMenuItem

        return () => {
            if (menuCloseTimeoutRef.current) {
                clearTimeout(menuCloseTimeoutRef.current)
            }
        }
    }, [isOpen, selectedMenuItem])

    return (
        <Box
            ref={menuContainerRef}
            onBlur={handleBlur}
            sx={{
                '.hidden-menu': {
                    visibility: 'hidden',
                },
                '.visible-menu': {
                    visibility: 'visible',
                },
            }}
        >
            <Box {...styles.listMenuTriggerContainer}>
                <Box
                    {...styles.listMenuTriggerGroup}
                    _focusVisible={{
                        border: remCalc(3),
                        borderStyle: 'solid',
                        borderColor: 'button.border.accessibility-blue-dark',
                        outline: 'unset',
                        boxShadow: 'unset',
                    }}
                    onClick={() => {
                        isKeyboardNavRef.current = false
                        if (isOpen) {
                            setSelectedMenuItem(null)
                            wasClosedWithNoTimeoutRef.current = true
                        } else {
                            setSelectedMenuItem(primarycategory.uid)
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Space') {
                            isKeyboardNavRef.current = true
                            if (isOpen) {
                                setSelectedMenuItem(null)
                                wasClosedWithNoTimeoutRef.current = true
                            } else {
                                setSelectedMenuItem(primarycategory.uid)
                            }
                        } else if (e.key === 'Escape') {
                            wasClosedWithNoTimeoutRef.current = true
                            setSelectedMenuItem(null)
                            isKeyboardNavRef.current = false
                        }
                    }}
                    role="button"
                    tabIndex="0"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={label}
                    id={`heading-${categoryID}`}
                >
                    <Text
                        {...(delayedIsOpen ? styles.listMenuTriggerLinkActive : {})}
                        {...{name: label + ' __'}}
                        aria-hidden="true"
                    >
                        {label}
                    </Text>

                    <IconButton
                        icon={
                            delayedIsOpen ? (
                                <HeaderUp boxSize="iconsSize.small" {...styles.chevronIcon} />
                            ) : (
                                <HeaderDown boxSize="iconsSize.small" {...styles.chevronIcon} />
                            )
                        }
                        background="transparent"
                        aria-hidden="true"
                        focusable="false"
                        pointerEvents="none"
                        tabIndex="-1"
                    />
                </Box>
            </Box>
            <Box
                data-testid={categoryID}
                className={delayedIsOpen ? 'visible-menu' : 'hidden-menu'}
            >
                <Box
                    {...styles.popoverOverlay}
                    onMouseEnter={() => {
                        // When mouse exits the menu area, only start the delayed closing if using mouse
                        if (isOpen && !menuCloseTimeoutRef.current && !isKeyboardNavRef.current) {
                            setSelectedMenuItem(null)
                        }
                    }}
                />
                <Slide
                    direction="top"
                    in={delayedIsOpen}
                    style={{position: 'absolute', top: '100%', zIndex: -1}}
                >
                    <Box
                        {...styles.listMenuContent}
                        onMouseEnter={() => {
                            // Cancel closing if user hovers back during delay
                            if (menuCloseTimeoutRef.current) {
                                clearTimeout(menuCloseTimeoutRef.current)
                                menuCloseTimeoutRef.current = null
                                if (!isOpen) {
                                    setSelectedMenuItem(primarycategory.uid)
                                }
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                wasClosedWithNoTimeoutRef.current = true
                                setSelectedMenuItem(null)
                                isKeyboardNavRef.current = false
                                const menuTrigger = document.getElementById(`heading-${categoryID}`)
                                if (menuTrigger) {
                                    menuTrigger.focus()
                                }
                            }
                        }}
                    >
                        <ListMenuContent
                            secondLevel={primarycategory?.second_level}
                            categoriesData={categoriesData}
                            categoryUrl={url}
                            isCategoryUrlAbsoulte={isAbsolute}
                            onItemClick={onItemClick}
                        />
                    </Box>
                </Slide>
            </Box>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    categoriesData: PropTypes.object,
    primarycategory: PropTypes.object,
    selectedMenuItem: PropTypes.string,
    setSelectedMenuItem: PropTypes.func,
    onItemClick: PropTypes.func,
}
export {ListMenuPopover}
